/**
 * Phase C — plan → schedule → delivery derivation, and the nightly drift
 * alert that is already written but never shown on the record.
 *
 * The monitor does not auto-heal. This module only reads and labels.
 */

export const SAH_SERVICE_STRUCTURAL_DRIFT_KIND = 'SAH_SERVICE_STRUCTURAL_DRIFT';

export const SAH_SERVICE_STRUCTURAL_DRIFT_HREF = '/home-care/schedules';

export type SahServiceDerivationCounts = {
  plannedServiceCount: number;
  scheduledCount?: number;
  deliveredCount?: number;
};

export type SahServiceStructuralDriftAlert = {
  id: string;
  createdAt: string;
  driftCount: number;
  sampleServiceIds: readonly string[];
  detail: string;
  owner: 'coordinator';
};

export function sahServiceStructuralDriftHref(clientId?: string | null): string {
  const id = String(clientId ?? '').trim();
  return id
    ? `${SAH_SERVICE_STRUCTURAL_DRIFT_HREF}?clientId=${encodeURIComponent(id)}`
    : SAH_SERVICE_STRUCTURAL_DRIFT_HREF;
}

export function carePlanScheduleProposalHref(carePlanVersionId: string): string {
  return `/roster/proposals/${carePlanVersionId}`;
}

export function isSahServiceStructuralDriftMetadata(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return metadata?.kind === SAH_SERVICE_STRUCTURAL_DRIFT_KIND;
}

export function parseSahServiceStructuralDriftAlert(row: {
  id: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}): SahServiceStructuralDriftAlert | null {
  if (!isSahServiceStructuralDriftMetadata(row.metadata)) return null;
  const driftCount = Number(row.metadata?.drift_count ?? 0);
  const sample = Array.isArray(row.metadata?.sample_services_delivered_ids)
    ? row.metadata.sample_services_delivered_ids.filter(
        (value): value is string => typeof value === 'string',
      )
    : [];
  const detail =
    typeof row.metadata?.detail === 'string'
      ? row.metadata.detail
      : 'Scheduled service rows no longer match roster re-derivation.';
  return {
    id: row.id,
    createdAt: row.created_at,
    driftCount: Number.isFinite(driftCount) ? driftCount : 0,
    sampleServiceIds: sample,
    detail,
    owner: 'coordinator',
  };
}

export function describeSahServiceDerivation(counts: SahServiceDerivationCounts): string {
  const planned = counts.plannedServiceCount;
  const scheduled = counts.scheduledCount;
  const delivered = counts.deliveredCount;
  if (scheduled == null || delivered == null) {
    return `${planned} planned service${planned === 1 ? '' : 's'}. Schedule and delivery counts are loading.`;
  }
  return `${planned} planned → ${scheduled} scheduled → ${delivered} delivered.`;
}

export function shouldPaintDerivationChain(input: {
  flagEnabled: boolean;
  hasDrift?: boolean;
  surface: 'delivery' | 'plan' | 'record';
}): boolean {
  if (!input.flagEnabled) return false;
  if (input.hasDrift) return true;
  // Plan first paint is the working copy. Delivery first paint is the next two
  // weeks (the tab already folds this card). Record may still show the chain.
  if (input.surface === 'plan') return false;
  return input.surface === 'delivery' || input.surface === 'record';
}

export function driftAlertTouchesClient(
  sampleServiceIds: readonly string[],
  clientServiceIds: readonly string[],
): boolean {
  if (sampleServiceIds.length === 0 || clientServiceIds.length === 0) return false;
  const owned = new Set(clientServiceIds);
  return sampleServiceIds.some((id) => owned.has(id));
}
